/**
 * The DOOH post-flight report as a template document — the V2 look, matching the V2 pre-flight.
 *
 * D36 makes post-flight DOOH a Release 1 requirement. It is also the driver for B-4: the venue table is
 * the one table in the catalog that can legitimately run to thousands of rows, because it lists every
 * screen that actually delivered.
 *
 * What it is written against:
 *
 *   - **Only real screen plays count**, and **impressions are audience, not plays (D38)**: the headline is
 *     Σ the SSP's per-play audience multiplier. The report shows IMPRESSIONS ONLY — plays are never
 *     printed (decided 2026-10-28). Mobile / web impressions the DOOH ads also won are excluded.
 *   - **Two screen-play sources.** The DOOH SSP's plays (Vistar, via `dooh_delivery`) fill the heatmap,
 *     the network table and the per-LOCATION table; the legacy `dooh_impressions` beacon (empty in
 *     production) fills the per-VENUE table. Each block shows only when its data exists.
 *   - **There is no CTR.** DOOH has no clicks; nothing is bound to a click figure.
 *   - **Over-delivery is shown as recorded** against the contract.
 */

import type { Template } from './template-schema.js';

/** Beacon-backed per-venue delivery exists (the legacy `dooh_impressions` source). */
const HAS_VENUES = '$count(dooh.venueDelivery) > 0';

export const DOOH_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-dooh-postflight',
  name: 'V2 — DOOH Post-Flight',
  version: 1,
  status: 'draft',
  agencyId: null,
  channels: ['DOOH'],
  lifecycle: ['INTERIM', 'POSTFLIGHT'],

  theme: {
    color: {
      primary: '#001345', accent: '#06CDF2', surface: '#FFFFFF', surfaceMuted: '#F5F5F5',
      border: '#E8E8E8', textPrimary: '#0A0A0A', textInverse: '#FFFFFF',
      headerFrom: '#000A1E', headerTo: '#00174B',
      page: '#F1F3F6', stripBorder: '#2B5FA8',
    },
    typography: { fontFamily: 'Roboto', baseSizePt: 11, scale: 1.21 },
    page: { widthIn: 8.5, heightIn: 11, marginIn: 0 },
    branding: { companyName: 'ProData Media', copyrightName: 'ProData Media' },
    iconStyle: 'v2',
    layout: { pageBackground: 'waves' },
  },

  chrome: {
    header: [{
      id: 'hdr', type: 'PageHeader',
      props: { title: 'Digital Out of Home - Post-Flight', showAccentRule: true },
      bindings: { clientName: 'campaign.clientName', campaignId: 'campaign.reportSo', logo: 'presentation.logo', showLogo: 'presentation.showLogo' },
    }],
    footer: [{
      id: 'ftr', type: 'PageFooter', props: {},
      // The copyright names the AGENCY (D: reports carry the agency's copyright, not the client's).
      bindings: { companyName: 'campaign.agencyName', year: '$substring(report.periodEnd, 0, 4)' },
    }],
  },

  pages: [
    // ------------------------------------------------------------- page 1 --
    {
      id: 'summary',
      mode: 'fixed',
      title: 'Campaign summary',
      blocks: [
        {
          // Creative left, details right — the same order as the other V2 reports.
          id: 'identity-row', type: 'Columns', props: { columns: 2 }, bindings: {},
          children: [
            {
              id: 'creative', type: 'CreativePreview',
              props: { maxHeightIn: 1.9, caption: '* Representative of creative used in campaign.', hideWhenEmpty: true },
              bindings: { assetId: 'campaign.bannerAssetId' },
            },
            {
              id: 'meta', type: 'CampaignMetaCard',
              props: { variant: 'labels', nameIcon: 'channel-dooh', showNote: true },
              bindings: {
                clientName: 'campaign.clientName', campaignName: 'campaign.name',
                startDate: 'campaign.startDate', endDate: 'campaign.endDate',
                campaignId: 'campaign.reportSo', note: 'campaign.notes',
              },
            },
          ],
        },
        {
          id: 'geo-card', type: 'SectionCard',
          props: { title: 'Geographic Targeting', icon: 'dooh-geo-targeting' }, bindings: {},
          children: [{
            id: 'geo-summary', type: 'RichTextBlock', props: { size: 'lead' },
            bindings: {
              html: `$count(targeting.cities) > 0 ? $join(targeting.cities, ", ")
                : $count(targeting.countyLabels) > 0 ? $join(targeting.countyLabels, ", ")
                : $count(targeting.dmas) > 0 ? $join(targeting.dmas, ", ")
                : $count(targeting.states) > 0 ? $join(targeting.states, ", ")
                : $count(targeting.zips) > 0
                  ? (targeting.radiusMiles ? ($string(targeting.radiusMiles) & " mile radius @ " & $join(targeting.zips, ", ")) : $join(targeting.zips, ", "))
                : "Targeting not specified"`,
            },
          }],
        },
        {
          // The headline pair: contracted vs delivered (audience) impressions.
          id: 'contracted-actual', type: 'ContractedVsActual',
          props: { contractedIcon: 'dooh-contracted-impressions', actualIcon: 'dooh-actual-impressions' },
          bindings: { contracted: 'overall.contract.targetImpressions', actual: 'overall.delivery.impressions' },
        },
        {
          /*
           * Delivering screens and over-delivery against the contract, shown as recorded. IMPRESSIONS ONLY:
           * plays are not shown anywhere on the report (decided 2026-10-28) — the headline audience figure is
           * the delivery measure, and plays stay in the data only as the basis of the weighting.
           */
          id: 'kpis', type: 'MetricTrio', props: {},
          bindings: {
            metrics: `[
              {"label": "Screens Delivering", "value": dooh.screenCount, "icon": "channel-dooh"},
              {"label": "Over-Delivered Impressions", "value": overall.derived.overDeliveredImpressions, "icon": "dooh-contracted-impressions"},
              {"label": "Over-Delivery", "value": overall.derived.overDeliveryPct, "format": "pct", "icon": "kpi-targeted-impressions"}
            ]`,
          },
        },
        {
          id: 'daily-card', type: 'SectionCard',
          props: { title: 'Impressions by Day', icon: 'calendar' },
          // A long flight is charted by week or month (the chart draws at most 40 points); say which.
          bindings: { title: `"Impressions by " & (dooh.impressionsSeriesUnit = "week" ? "Week" : dooh.impressionsSeriesUnit = "month" ? "Month" : "Day")` },
          visibleWhen: '$count(dooh.impressionsByDay) > 0',
          children: [{
            id: 'daily-line', type: 'SeriesChart',
            props: { chartType: 'line', heightIn: 1.9, valueFormat: 'int', showLegend: false, yLabel: 'Impressions' },
            bindings: {
              categories: 'dooh.impressionsByDay.label',
              series: `[{"name": "Impressions", "values": dooh.impressionsByDay.impressions}]`,
            },
          }],
        },
        /*
         * Unmatched screens are disclosed on the summary page, not buried. A `screen_id` with no match in
         * `dooh_screens` means the inventory import has not caught up (D39); its delivery still counts.
         */
        {
          id: 'unmatched', type: 'RichTextBlock', props: { size: 'note' },
          bindings: {
            html: `"<b>" & $string(dooh.unmatchedScreens) & "</b> of <b>" & $string(dooh.screenCount)
                   & "</b> delivering screens are not yet present in the screen inventory. Their delivery IS "
                   & "counted in the totals above; their venue details will resolve once the inventory import runs."`,
          },
          visibleWhen: 'dooh.unmatchedScreens > 0',
        },
      ],
    },

    // ------------------------------------------------------------- page 2+ --
    /*
     * Where it ran: the screen map, the venue-type mix, and — with screen plays — the per-venue table.
     * `flowing`, so the venue table splits across as many sheets as the data needs with a repeating header
     * (B-4: thousands of rows on a large buy).
     */
    {
      id: 'venues',
      mode: 'flowing',
      title: 'Venue delivery',
      blocks: [
        {
          id: 'map-card', type: 'SectionCard',
          props: { title: 'Screen Locations', icon: 'pin' }, bindings: {},
          visibleWhen: 'dooh.mapAssetId',
          children: [{
            id: 'pin-map', type: 'PinMap', props: { heightIn: 3.4 },
            bindings: { mapAssetId: 'dooh.mapAssetId' },
          }],
        },
        {
          id: 'venue-summary-card', type: 'SectionCard',
          props: { title: 'Delivery by Venue Type' }, bindings: {},
          visibleWhen: HAS_VENUES,
          children: [{
            id: 'venue-summary', type: 'VenueTypeSummary', props: { columns: 3, noun: 'delivering screens' },
            bindings: { rows: 'dooh.venueSummary', total: 'dooh.screenCount' },
          }],
        },
        {
          // When the creative played — US-Eastern day × hour, shaded by weighted impressions (as on CTV).
          id: 'daytime-card', type: 'SectionCard',
          props: { title: 'Views by Day & Time', icon: 'calendar' }, bindings: {},
          visibleWhen: '$count(dooh.viewsByDayHour) > 0',
          children: [{
            id: 'daytime-heatmap', type: 'DayHourHeatmap',
            props: { caption: 'Hour of day (US Eastern) · shade = impressions' },
            bindings: { matrix: 'dooh.viewsByDayHour' },
          }],
        },
        {
          id: 'network-card', type: 'SectionCard',
          props: { title: 'Delivery by Network' }, bindings: {},
          visibleWhen: '$count(dooh.networkDelivery) > 0',
          children: [{
            id: 'network-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'name', label: 'Screen Network', widthPct: 56 },
                { key: 'screens', label: 'Screens', widthPct: 20 },
                { key: 'impressions', label: 'Impressions', widthPct: 24 },
              ]),
              zebra: true, maxRows: 100000, showingOf: false,
            },
            bindings: {
              rows: `dooh.networkDelivery.{
                "name": name,
                "screens": $formatNumber(screens, "#,##0"),
                "impressions": $formatNumber(impressions, "#,##0")
              }`,
              totalRows: '$count(dooh.networkDelivery)',
            },
          }],
        },
        {
          // Every screen location that played (uncapped — measured delivery), largest audience first.
          id: 'location-card', type: 'SectionCard',
          props: { title: 'Delivery by Screen Location' }, bindings: {},
          visibleWhen: '$count(dooh.locationDelivery) > 0',
          children: [{
            id: 'location-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'network', label: 'Screen Network', widthPct: 42 },
                { key: 'place', label: 'Location', widthPct: 38 },
                { key: 'impressions', label: 'Impressions', widthPct: 20 },
              ]),
              zebra: true, maxRows: 100000, showingOf: true,
            },
            bindings: {
              rows: `dooh.locationDelivery.{
                "network": network,
                "place": cityStateZip,
                "impressions": $formatNumber(impressions, "#,##0")
              }`,
              totalRows: '$count(dooh.locationDelivery)',
            },
          }],
        },
        {
          id: 'venue-card', type: 'SectionCard',
          props: { title: 'Delivery by Venue' }, bindings: {},
          visibleWhen: HAS_VENUES,
          children: [{
            id: 'venue-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'venueName', label: 'Venue', widthPct: 32 },
                { key: 'venueType', label: 'Venue Type', widthPct: 26 },
                { key: 'cityStateZip', label: 'Location', widthPct: 24 },
                { key: 'impressions', label: 'Impressions', widthPct: 18 },
              ]),
              zebra: true,
              // Every delivering venue appears. Unlike the pre-flight placement list, which D42 caps at 100,
              // this is measured delivery the client paid for — capping it would understate the campaign and
              // make the report's own totals disagree with its table.
              maxRows: 100000,
              showingOf: true,
            },
            bindings: {
              rows: `dooh.venueDelivery.{
                "venueName": venueName & (unmatchedScreen ? " *" : ""),
                "venueType": venueType,
                "cityStateZip": cityStateZip,
                "impressions": $formatNumber(impressions, "#,##0")
              }`,
              totalRows: '$count(dooh.venueDelivery)',
            },
          }],
        },
      ],
    },
  ],

  createdAt: '2026-08-16T00:00:00.000Z',
  createdBy: 'm3-pagination',
};
